/**
 * Every tour id. Each is registered in a host app's tour registry, and each one
 * the assistant may offer is also described in `tourCatalogue.ts`, which types its
 * own exhaustiveness off this list.
 */
export const TOUR_IDS = [
  'homepage-cards-manage-data',
  'component-overview',
  'data-inventory',
  'data-inventory-whats-new-2026-10',
  'discover-experience',
  'presentation-navigator',
  'answer-surveys',
  'improved-datasets-panel',
] as const;

export type TourId = (typeof TOUR_IDS)[number];

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right';

/** A viewport-relative box, as measured by `getBoundingClientRect`. */
export type Rect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export type Size = { width: number; height: number };

/**
 * Step copy as data rather than JSX: a plain string, or a list of segments
 * where `{ bold }` renders emphasised inline. Keeping copy as data avoids
 * `no-hardcoded-strings` JSX-text lint and centralises styling in `TourCard`.
 */
export type TourBodySegment = string | { bold: string };
export type TourBody = string | TourBodySegment[];

export type TourStep = {
  id: string;
  /**
   * CSS selector for the element to anchor the popover to. When `null` the
   * step renders as a centered modal (used for the intro / outro steps).
   */
  target: string | null;
  /**
   * CSS selector for a second element to highlight alongside `target` — for a
   * step whose copy points at two things ("write a description, then save").
   */
  secondaryTarget?: string;
  /**
   * Draw `target` and `secondaryTarget` as one highlight around both, for two
   * small neighbouring elements that read as a single control.
   */
  isHighlightMerged?: boolean;
  title?: string;
  body: TourBody;
  /** Preferred placement of the popover relative to the target. */
  placement?: TourPlacement;
  /**
   * How much of the page the user may interact with while this step is shown.
   * `'all'` lets pointer events through to the page. `'hover'` lets hover
   * through but swallows clicks, for a step pointing at something the user
   * should preview but not navigate away through; clicks on the tour card
   * itself still work. Defaults to `'none'`.
   */
  allowInteraction?: 'all' | 'hover' | 'none';
  /**
   * Set when the anchor rides in on an ancestor animation or reflow (e.g. a
   * drawer sliding open) rather than being in place the moment it appears. The
   * engine then re-measures the anchor for a short window and drops the
   * spotlight's glide transition so it follows the anchor frame-by-frame
   * instead of trailing it. Leave unset for anchors that are already settled.
   */
  trackMovingAnchor?: boolean;
  // Set on a step there is no going back from — the step a hand-off lands on,
  isForwardStepOnly?: boolean;
  /** Set where `beforeShow` focuses the anchor to reveal it; the card then leaves focus alone. */
  pageKeepsFocus?: boolean;
  /**
   * Runs before the step is shown. Use it to navigate, open a menu/palette or
   * scroll. The engine then waits for `target` to appear in the DOM before
   * rendering the popover.
   */
  beforeShow?: () => Promise<void> | void;
};

export type TourDefinition = {
  id: TourId;
  steps: TourStep[];
  /** Accessible name of the tour dialog; see `TourCard`. Defaults to "Product tour". */
  dialogLabel?: string;
  /**
   * Identifies the data this definition was built from, for a tour that loads
   * data while it runs. When it changes, the engine resolves the current step
   * again — a step launched before its data arrived (a URL that starts the tour
   * partway through) then gets another go with the data in hand.
   *
   * It must change only when data a `beforeShow` depends on changes: it is the
   * tour saying "this step could go better now", and a value that churns
   * re-runs `beforeShow` under the user. Leave it unset for a tour with no
   * runtime data — its steps are resolved once, as before.
   */
  dataRevision?: string;
};

/**
 * A tour step as produced by the assistant: copy is a plain string rather than
 * the richer `TourBody`, and it is not tied to a registered `TourId`.
 */
export type GeneratedTourStep = {
  id: string;
  target: string | null;
  title: string;
  body: string;
  placement?: TourPlacement;
};

export type ActiveTour = {
  definition: TourDefinition;
  stepIndex: number;
};
